import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  FormControl,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Chip,
  Button,
  Select,
  MenuItem,
  InputLabel,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Clear as ClearIcon,
  FilterList as FilterListIcon,
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { useTranslation } from 'react-i18next';

interface SearchFiltersProps {
  onFiltersChange: (filters: SearchFiltersType) => void;
  initialFilters?: SearchFiltersType;
}

export interface SearchFiltersType {
  categories: string[];
  fileTypes: string[];
  dateRange: {
    start: Date | null;
    end: Date | null;
  };
  tags: string[];
  uploadedBy: string[];
  searchType: 'basic' | 'semantic' | 'exact';
  sortBy: 'relevance' | 'date' | 'title' | 'size';
  sortOrder: 'asc' | 'desc';
}

const defaultFilters: SearchFiltersType = {
  categories: [],
  fileTypes: [],
  dateRange: { start: null, end: null },
  tags: [],
  uploadedBy: [],
  searchType: 'basic',
  sortBy: 'relevance',
  sortOrder: 'desc',
};

const availableCategories = ['Finance', 'Planning', 'Analytics', 'HR', 'Marketing', 'Engineering', 'Legal', 'Operations'];
const availableFileTypes = ['pdf', 'docx', 'xlsx', 'pptx', 'txt', 'csv'];
const availableTags = ['quarterly', 'report', 'strategy', 'roadmap', 'analytics', 'dashboard', '2024', 'confidential', 'public', 'draft'];
const availableUsers = ['John Doe', 'Jane Smith', 'Mike Johnson', 'Sarah Williams', 'David Brown', 'Lisa Davis'];

const SearchFilters: React.FC<SearchFiltersProps> = ({
  onFiltersChange,
  initialFilters = defaultFilters,
}) => {
  const { t } = useTranslation();
  const [filters, setFilters] = useState<SearchFiltersType>(initialFilters);
  const [expanded, setExpanded] = useState<string | false>('search-type');

  const handleFilterChange = (newFilters: Partial<SearchFiltersType>) => {
    const updatedFilters = { ...filters, ...newFilters };
    setFilters(updatedFilters);
    onFiltersChange(updatedFilters);
  };

  const handleArrayFilterChange = (
    filterKey: keyof Pick<SearchFiltersType, 'categories' | 'fileTypes' | 'tags' | 'uploadedBy'>,
    value: string,
    checked: boolean
  ) => {
    const currentArray = filters[filterKey] as string[];
    const newArray = checked
      ? [...currentArray, value]
      : currentArray.filter(item => item !== value);

    handleFilterChange({ [filterKey]: newArray });
  };

  const handleClearAll = () => {
    setFilters(defaultFilters);
    onFiltersChange(defaultFilters);
  };

  const hasActiveFilters = () => {
    return (
      filters.categories.length > 0 ||
      filters.fileTypes.length > 0 ||
      filters.tags.length > 0 ||
      filters.uploadedBy.length > 0 ||
      filters.dateRange.start ||
      filters.dateRange.end ||
      filters.searchType !== 'basic' ||
      filters.sortBy !== 'relevance'
    );
  };

  const getActiveFilterCount = () => {
    return filters.categories.length +
           filters.fileTypes.length +
           filters.tags.length +
           filters.uploadedBy.length +
           (filters.dateRange.start ? 1 : 0) +
           (filters.dateRange.end ? 1 : 0) +
           (filters.searchType !== 'basic' ? 1 : 0) +
           (filters.sortBy !== 'relevance' ? 1 : 0);
  };

  const handleAccordionChange = (panel: string) => (_event: React.SyntheticEvent, isExpanded: boolean) => {
    setExpanded(isExpanded ? panel : false);
  };

  return (
    <Paper elevation={1} sx={{ p: 2 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Box display="flex" alignItems="center">
          <FilterListIcon sx={{ mr: 1 }} />
          <Typography variant="h6">
            {t('search.filters')}
            {hasActiveFilters() && (
              <Chip
                label={getActiveFilterCount()}
                size="small"
                color="primary"
                sx={{ ml: 1 }}
              />
            )}
          </Typography>
        </Box>
        {hasActiveFilters() && (
          <Button
            startIcon={<ClearIcon />}
            onClick={handleClearAll}
            size="small"
            color="secondary"
          >
            {t('common.clearAll')}
          </Button>
        )}
      </Box>

      {/* Search Type */}
      <Accordion expanded={expanded === 'search-type'} onChange={handleAccordionChange('search-type')}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography>{t('search.searchType')}</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <FormControl fullWidth size="small">
            <InputLabel>{t('search.searchType')}</InputLabel>
            <Select
              value={filters.searchType}
              onChange={(e) => handleFilterChange({ searchType: e.target.value as any })}
              label={t('search.searchType')}
            >
              <MenuItem value="basic">{t('search.searchTypes.basic')}</MenuItem>
              <MenuItem value="semantic">{t('search.searchTypes.semantic')}</MenuItem>
              <MenuItem value="exact">{t('search.searchTypes.exact')}</MenuItem>
            </Select>
          </FormControl>
        </AccordionDetails>
      </Accordion>

      {/* Sort Options */}
      <Accordion expanded={expanded === 'sort'} onChange={handleAccordionChange('sort')}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography>{t('search.sortOptions')}</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Box display="flex" gap={2}>
            <FormControl fullWidth size="small">
              <InputLabel>{t('search.sortBy')}</InputLabel>
              <Select
                value={filters.sortBy}
                onChange={(e) => handleFilterChange({ sortBy: e.target.value as any })}
                label={t('search.sortBy')}
              >
                <MenuItem value="relevance">{t('search.sortValues.relevance')}</MenuItem>
                <MenuItem value="date">{t('documents.sortOptions.date')}</MenuItem>
                <MenuItem value="title">{t('search.sortValues.title')}</MenuItem>
                <MenuItem value="size">{t('documents.sortOptions.size')}</MenuItem>
              </Select>
            </FormControl>
            <FormControl fullWidth size="small">
              <InputLabel>{t('search.order')}</InputLabel>
              <Select
                value={filters.sortOrder}
                onChange={(e) => handleFilterChange({ sortOrder: e.target.value as any })}
                label={t('search.order')}
              >
                <MenuItem value="desc">{t('search.sortOrder.desc')}</MenuItem>
                <MenuItem value="asc">{t('search.sortOrder.asc')}</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </AccordionDetails>
      </Accordion>

      {/* Categories */}
      <Accordion expanded={expanded === 'categories'} onChange={handleAccordionChange('categories')}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography>
            {t('documents.categories')}
            {filters.categories.length > 0 && (
              <Chip label={filters.categories.length} size="small" sx={{ ml: 1 }} />
            )}
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <FormGroup>
            {availableCategories.map((category) => (
              <FormControlLabel
                key={category}
                control={
                  <Checkbox
                    checked={filters.categories.includes(category)}
                    onChange={(e) => handleArrayFilterChange('categories', category, e.target.checked)}
                    size="small"
                  />
                }
                label={category}
              />
            ))}
          </FormGroup>
        </AccordionDetails>
      </Accordion>

      {/* File Types */}
      <Accordion expanded={expanded === 'file-types'} onChange={handleAccordionChange('file-types')}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography>
            {t('documents.fileTypes')}
            {filters.fileTypes.length > 0 && (
              <Chip label={filters.fileTypes.length} size="small" sx={{ ml: 1 }} />
            )}
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <FormGroup>
            {availableFileTypes.map((fileType) => (
              <FormControlLabel
                key={fileType}
                control={
                  <Checkbox
                    checked={filters.fileTypes.includes(fileType)}
                    onChange={(e) => handleArrayFilterChange('fileTypes', fileType, e.target.checked)}
                    size="small"
                  />
                }
                label={fileType.toUpperCase()}
              />
            ))}
          </FormGroup>
        </AccordionDetails>
      </Accordion>

      {/* Tags */}
      <Accordion expanded={expanded === 'tags'} onChange={handleAccordionChange('tags')}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography>
            {t('documents.tags')}
            {filters.tags.length > 0 && (
              <Chip label={filters.tags.length} size="small" sx={{ ml: 1 }} />
            )}
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <FormGroup>
            {availableTags.map((tag) => (
              <FormControlLabel
                key={tag}
                control={
                  <Checkbox
                    checked={filters.tags.includes(tag)}
                    onChange={(e) => handleArrayFilterChange('tags', tag, e.target.checked)}
                    size="small"
                  />
                }
                label={tag}
              />
            ))}
          </FormGroup>
        </AccordionDetails>
      </Accordion>

      {/* Date Range */}
      <Accordion expanded={expanded === 'date-range'} onChange={handleAccordionChange('date-range')}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography>
            {t('documents.dateRange')}
            {(filters.dateRange.start || filters.dateRange.end) && (
              <Chip label={t('search.active')} size="small" sx={{ ml: 1 }} />
            )}
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <Box display="flex" flexDirection="column" gap={2}>
              <DatePicker
                label={t('documents.startDate')}
                value={filters.dateRange.start}
                onChange={(date) => handleFilterChange({
                  dateRange: { ...filters.dateRange, start: date }
                })}
                slotProps={{
                  textField: { size: 'small', fullWidth: true }
                }}
              />
              <DatePicker
                label={t('documents.endDate')}
                value={filters.dateRange.end}
                onChange={(date) => handleFilterChange({
                  dateRange: { ...filters.dateRange, end: date }
                })}
                slotProps={{
                  textField: { size: 'small', fullWidth: true }
                }}
              />
            </Box>
          </LocalizationProvider>
        </AccordionDetails>
      </Accordion>

      {/* Uploaded By */}
      <Accordion expanded={expanded === 'uploaded-by'} onChange={handleAccordionChange('uploaded-by')}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography>
            {t('documents.uploadedBy')}
            {filters.uploadedBy.length > 0 && (
              <Chip label={filters.uploadedBy.length} size="small" sx={{ ml: 1 }} />
            )}
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <FormGroup>
            {availableUsers.map((user) => (
              <FormControlLabel
                key={user}
                control={
                  <Checkbox
                    checked={filters.uploadedBy.includes(user)}
                    onChange={(e) => handleArrayFilterChange('uploadedBy', user, e.target.checked)}
                    size="small"
                  />
                }
                label={user}
              />
            ))}
          </FormGroup>
        </AccordionDetails>
      </Accordion>

      {/* Active Filters Summary */}
      {hasActiveFilters() && (
        <Box mt={2} p={2} bgcolor="action.hover" borderRadius={1}>
          <Typography variant="subtitle2" gutterBottom>
            {t('search.activeFilters')}:
          </Typography>
          <Box display="flex" flexWrap="wrap" gap={0.5}>
            {filters.categories.map((category) => (
              <Chip
                key={`cat-${category}`}
                label={category}
                size="small"
                onDelete={() => handleArrayFilterChange('categories', category, false)}
              />
            ))}
            {filters.fileTypes.map((fileType) => (
              <Chip
                key={`file-${fileType}`}
                label={fileType.toUpperCase()}
                size="small"
                onDelete={() => handleArrayFilterChange('fileTypes', fileType, false)}
              />
            ))}
            {filters.tags.map((tag) => (
              <Chip
                key={`tag-${tag}`}
                label={tag}
                size="small"
                onDelete={() => handleArrayFilterChange('tags', tag, false)}
              />
            ))}
            {filters.uploadedBy.map((user) => (
              <Chip
                key={`user-${user}`}
                label={user}
                size="small"
                onDelete={() => handleArrayFilterChange('uploadedBy', user, false)}
              />
            ))}
          </Box>
        </Box>
      )}
    </Paper>
  );
};

export default SearchFilters;
